import React, { useEffect, useMemo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import localforage from "localforage";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import Hls from "hls.js";

import {
  PlayerStore,
  psChangeAudioData,
  psPauseAudio,
  psPlayAudio,
} from "@/store/PlayerStore";
import { RadioStore } from "@/store/RadioStore";
import { apiPath } from "@/helpers/apiPath";
import {
  navigatorArtworkSize,
  ScheduleItemInterface,
} from "@/helpers/pageHelpers/Common/interface";
import client from "@/helpers/apiClient";
import { updateNavigator } from "@/helpers/updateNavigator";
import { useNowPlaying } from "@/helpers/useNowPlaying";

dayjs.extend(isBetween);

export const RadioWrapper: React.FC = () => {
  const [scheduleInfo, setScheduleInfo] = useState<ScheduleItemInterface>();
  const [nowPlaying, setNowPlaying] = useState<string>("");
  const { nowPlayingData, mutate } = useNowPlaying();

  const isClientSide = typeof window !== "undefined";

  useMemo(async () => {
    nowPlaying && (await mutate());
  }, [nowPlaying]);

  const clearScheduleData = () => {
    localforage.setItem("scheduleData", null).then(() => {
      setScheduleInfo(null);
    });

    PlayerStore.update((store) => {
      store.isSchedulePlaying = false;
      store.scheduleBelongsToPortal = false;
      store.isScheduleAvailable = false;
      store.scheduleTitle = "";
      store.scheduleArtworkUrl = "";
      store.scheduleSlug = "";
      store.scheduleStart = "";
      store.scheduleEnd = "";
    });
  };

  const updatePlayerInfo = ({ artist, title, artworkUrl, artworkUrlLarge }) => {
    PlayerStore.update((store) => {
      store.radioTitle = artist;
      store.radioSubTitle = title;
      store.radioArtwork = artworkUrl;
      store.radioArtworkLarge = artworkUrlLarge;
    });

    RadioStore.update((store) => {
      store.currentTrack = title;
    });
  };

  const updateScheduleInfo = ({ endTime, startTime, podcast }) => {
    PlayerStore.update((store) => {
      store.isSchedulePlaying = true;
      store.isScheduleAvailable = true;
      store.scheduleTitle = podcast.title;
      store.scheduleArtworkUrl = podcast.artwork?.desktop;
      store.scheduleSlug = podcast.slug;
      store.scheduleStart = startTime;
      store.scheduleEnd = endTime;
      store.radioArtwork = nowPlayingData?.artworkUrl;
      store.radioArtworkLarge = nowPlayingData?.artworkUrlLarge;
      store.scheduleBelongsToPortal = podcast.belongsToPortal;
    });
  };

  // Get daily schedule details
  const getDailySchedule = () => {
    client.get(apiPath.DAILY_SCHEDULE).then((response) => {
      const dailySchedule = response.data;

      if (response.status === 200 && !!dailySchedule?.scheduleItems.length) {
        localforage.setItem("dailySchedule", dailySchedule.scheduleItems);
        getScheduleInfo(dailySchedule.scheduleItems);
      }
    });
  };

  const getCurrentScheduleData = () => {
    localforage
      .getItem("dailySchedule")
      .then((items: ScheduleItemInterface[]) => {
        getScheduleInfo(items);
      });
  };

  const getScheduleInfo = (items) => {
    const currentSchedule =
      items &&
      items?.find((item) =>
        dayjs().isBetween(dayjs(item.startTime), dayjs(item.endTime))
      );

    if (currentSchedule) {
      setScheduleInfo(currentSchedule);
    } else {
      clearScheduleData();
    }
  };

  const getDetailsWithInterval = () => {
    setInterval(() => {
      getCurrentScheduleData();
    }, 15000);
  };

  useEffect(() => {
    nowPlayingData && updatePlayerInfo(nowPlayingData);
  }, [nowPlayingData]);

  // Mount audio element and get initial data for Radio & Set Default Audio values
  // Run API calls with intervals and get tracks history
  useEffect(() => {
    if (isClientSide) {
      if (window.ReactNativeWebView && window.RNisNewRadio) {
        PlayerStore.update((store) => {
          store.audioElement = {
            pause: async () => {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ event: "pause" })
              );
            },
            play: async () => {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ event: "play" })
              );
            },
            setCurrentTime: async (time) => {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ event: "setCurrentTime", time })
              );
            },
            setSource: async (source, metadata, time) => {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ event: "setSource", source, metadata, time })
              );
            },
            setVolume: async (volume) => {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ event: "setVolume", volume })
              );
            },
            setRate: async (rate) => {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ event: "setRate", rate })
              );
            },
            updateMetadata: async (metadata) => {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ event: "updateMetadata", metadata })
              );
            },
          };
          const setAudioData = (event) => {
            const { duration, currentTime } = event;
            const audioDuration = Math.round(duration || 0);
            const roundedCurrentTime = Math.round(currentTime);
            PlayerStore.update(
              psChangeAudioData(audioDuration, roundedCurrentTime)
            );
          };

          const playAudioListener = () => {
            PlayerStore.update(psPlayAudio);
          };

          const pauseAudioListener = () => {
            PlayerStore.update(psPauseAudio);
          };

          const endAudioListener = (event) => {
            const { duration, currentTime } = event;
            PlayerStore.update(psPauseAudio);
            const audioDuration = Math.round(duration || 0);
            const roundedCurrentTime = Math.round(currentTime);
            PlayerStore.update(
              psChangeAudioData(audioDuration, roundedCurrentTime)
            );
          };

          const updateMetadata = () => {
            mutate();
          };

          document.body.addEventListener("rntimeupdate", setAudioData);
          document.body.addEventListener("rnpause", pauseAudioListener);
          document.body.addEventListener("rnplay", playAudioListener);
          document.body.addEventListener("rnended", endAudioListener);
          document.body.addEventListener("rnupdateMetadata", updateMetadata);
        });
      } else {
        const audioTag = document.createElement("audio");
        const wrapper = document.getElementById("audio-wrapper");
        const hls = new Hls({ backBufferLength: 0 });

        audioTag.id = "audio-player";
        audioTag.className = "mejs__player";
        audioTag.preload = "none";
        audioTag.controls = true;
        audioTag.src = "";
        audioTag.volume = 0.5;

        wrapper.appendChild(audioTag);

        hls.on(Hls.Events.BUFFER_APPENDED, (_, data) => {
          setNowPlaying(data?.frag?.title);
        });

        const setAudioData = () => {
          const audioDuration = Math.round(audioTag.duration || 0);
          const currentTime = Math.round(audioTag.currentTime);
          PlayerStore.update(psChangeAudioData(audioDuration, currentTime));
        };

        const playAudioListener = () => {
          PlayerStore.update(psPlayAudio);
        };

        const pauseAudioListener = () => {
          PlayerStore.update(psPauseAudio);
        };

        const endAudioListener = () => {
          PlayerStore.update(psPauseAudio);
          const audioDuration = Math.round(audioTag.duration || 0);
          const currentTime = Math.round(audioTag.currentTime);
          PlayerStore.update(psChangeAudioData(audioDuration, currentTime));
        };

        audioTag.addEventListener("timeupdate", setAudioData);
        audioTag.addEventListener("pause", pauseAudioListener);
        audioTag.addEventListener("play", playAudioListener);
        audioTag.addEventListener("ended", endAudioListener);

        PlayerStore.update((store) => {
          store.audioElement = {
            pause: async () => audioTag.pause(),
            play: async () => audioTag.play(),
            setCurrentTime: async (time) => {
              if (time) {
                audioTag.currentTime = time;
              }
            },
            setSource: async (source) => {
              if (
                (source === process.env.NEXT_PUBLIC_RADIO_SOURCE ||
                  source ===
                    process.env.NEXT_PUBLIC_RADIO_SBM_FFESTIVAL_SOURCE) &&
                Hls.isSupported()
              ) {
                hls.attachMedia(audioTag);
                hls.loadSource(source);
              } else {
                hls.detachMedia();
                audioTag.src = source;
              }
            },
            setVolume: async (volume) => {
              audioTag.volume = volume;
            },
            setRate: async (rate) => {
              audioTag.playbackRate = rate;
            },
            updateMetadata: async (metadata) => {
              updateNavigator({
                ...metadata,
                size: navigatorArtworkSize.Large,
              });
            },
          };
        });
      }

      localforage
        .getItem("scheduleData")
        .then((value: ScheduleItemInterface) => {
          setScheduleInfo(value);
        });
    }

    getDailySchedule();
    getDetailsWithInterval();
  }, []);

  // Update Stores when initial data comes
  useEffect(() => {
    if (!!scheduleInfo) {
      updateScheduleInfo(scheduleInfo);
    }
  }, [scheduleInfo]);

  // Update Stores based on Schedule availability
  useEffect(() => {
    const isScheduleEmpty = isEmpty(scheduleInfo);

    !isScheduleEmpty && updateScheduleInfo(scheduleInfo);
  }, [scheduleInfo]);

  return null;
};

export default RadioWrapper;
